import React, { useState, useEffect } from 'react';
import { useAuth } from '../../components/Auth/AuthContext';
import { useApp } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { MapPin, Calendar, Camera, MessageCircle, Clock, Star, Cloud, Eye, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';

export default function VisitedSpotsSection() {
  const { user, token } = useAuth();
  const { actions } = useApp();
  const [spotVisits, setSpotVisits] = useState([]); // Grouped visits by spot
  const [loading, setLoading] = useState(true);
  const [expandedSpots, setExpandedSpots] = useState({}); // Track which spots are expanded
  
  useEffect(() => {
    const fetchVisitedSpots = async () => {
      // Always try to get a token from localStorage first
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken && !user?.id) {
        console.log('No token or user found, showing demo data');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('Fetching visited spots...');
        
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/visited-spots`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const spotsData = await response.json();
          console.log('Visited spots data received:', spotsData);
          
          if (spotsData && spotsData.length > 0) {
            // Transform the backend data structure to match frontend expectations
            // Backend returns spots with visit_history, we need to convert to {spot, visited} format
            const transformedData = spotsData.map(spotData => ({
              spot: spotData,
              visited: spotData.visit_history || []
            }));
            
            // Filter only spots that have visits
            const spotsWithVisits = transformedData.filter(spotData => 
              spotData.visited && Array.isArray(spotData.visited) && spotData.visited.length > 0
            );
            
            // Group visits by spot and calculate statistics
            const groupedVisits = spotsWithVisits.map(spotData => {
              const visits = spotData.visited || [];
              
              // Sort visits by visit_date (most recent first)
              const sortedVisits = visits.sort((a, b) => {
                const dateA = new Date(a.visit_date || a.created_at);
                const dateB = new Date(b.visit_date || b.created_at);
                return dateB.getTime() - dateA.getTime();
              });

              // Calculate first and last visit dates
              const firstVisit = sortedVisits[sortedVisits.length - 1];
              const lastVisit = sortedVisits[0];
              
              return {
                spot: spotData.spot,
                visits: sortedVisits,
                visitCount: visits.length,
                firstVisitDate: firstVisit ? new Date(firstVisit.visit_date || firstVisit.created_at) : null,
                lastVisitDate: lastVisit ? new Date(lastVisit.visit_date || lastVisit.created_at) : null
              };
            });
            
            // Sort spots by last visit date (most recent first)
            const sortedSpotVisits = groupedVisits.sort((a, b) => {
              if (!a.lastVisitDate) return 1;
              if (!b.lastVisitDate) return -1;
              return b.lastVisitDate.getTime() - a.lastVisitDate.getTime();
            });
            
            setSpotVisits(sortedSpotVisits);
          } else {
            console.log('No visited spots found, enabling debug mode with sample data');
            // Show sample data for demonstration
            const sampleSpotData = [{
              id: "demo1",
              name: "Lac de montagne paisible",
              description: "Un magnifique lac entouré de montagnes avec une vue panoramique exceptionnelle.",
              visit_history: [
                {
                  id: "visit1",
                  visit_date: "2025-08-21T06:00:00",
                  description: "Test visite depuis API",
                  rating: 5,
                  weather: "sunny"
                },
                {
                  id: "visit2", 
                  visit_date: "2025-08-15T14:30:00",
                  description: "Deuxième visite avec des amis - magnifique coucher de soleil !",
                  rating: 4,
                  weather: "partly-cloudy"
                }
              ]
            }];
            
            // Transform sample data to match expected format
            const transformedSampleData = sampleSpotData.map(spotData => ({
              spot: spotData,
              visited: spotData.visit_history || []
            }));
            
            const groupedSampleVisits = transformedSampleData.map(spotData => {
              const visits = spotData.visited || [];
              
              const sortedVisits = visits.sort((a, b) => {
                const dateA = new Date(a.visit_date || a.created_at);
                const dateB = new Date(b.visit_date || b.created_at);
                return dateB.getTime() - dateA.getTime();
              });

              const firstVisit = sortedVisits[sortedVisits.length - 1];
              const lastVisit = sortedVisits[0];
              
              return {
                spot: spotData.spot,
                visits: sortedVisits,
                visitCount: visits.length,
                firstVisitDate: firstVisit ? new Date(firstVisit.visit_date || firstVisit.created_at) : null,
                lastVisitDate: lastVisit ? new Date(lastVisit.visit_date || lastVisit.created_at) : null
              };
            });
            
            setSpotVisits(groupedSampleVisits);
          }
        } else {
          console.log('API request failed with status:', response.status);
          throw new Error(`HTTP ${response.status}`);
        }
      } catch (error) {
        console.error('Error fetching visited spots:', error);
        console.log('API failed, enabling debug mode with sample data');
        // Show sample data when API fails
        const sampleSpotData = [{
          id: "demo1",
          name: "Lac de montagne paisible",
          description: "Un magnifique lac entouré de montagnes avec une vue panoramique exceptionnelle.",
          visit_history: [
            {
              id: "visit1",
              visit_date: "2025-08-21T06:00:00",
              description: "Test visite depuis API",
              rating: 5,
              weather: "sunny"
            },
            {
              id: "visit2", 
              visit_date: "2025-08-15T14:30:00",
              description: "Deuxième visite avec des amis - magnifique coucher de soleil !",
              rating: 4,
              weather: "partly-cloudy"
            }
          ]
        }];
        
        // Transform sample data to match expected format
        const transformedSampleData = sampleSpotData.map(spotData => ({
          spot: spotData,
          visited: spotData.visit_history || []
        }));
        
        const groupedSampleVisits = transformedSampleData.map(spotData => {
          const visits = spotData.visited || [];
          
          const sortedVisits = visits.sort((a, b) => {
            const dateA = new Date(a.visit_date || a.created_at);
            const dateB = new Date(b.visit_date || b.created_at);
            return dateB.getTime() - dateA.getTime();
          });

          const firstVisit = sortedVisits[sortedVisits.length - 1];
          const lastVisit = sortedVisits[0];
          
          return {
            spot: spotData.spot,
            visits: sortedVisits,
            visitCount: visits.length,
            firstVisitDate: firstVisit ? new Date(firstVisit.visit_date || firstVisit.created_at) : null,
            lastVisitDate: lastVisit ? new Date(lastVisit.visit_date || lastVisit.created_at) : null
          };
        });
        
        setSpotVisits(groupedSampleVisits);
      } finally {
        setLoading(false);
      }
    };

    fetchVisitedSpots();
  }, [user]);



  const formatVisitDate = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      time: date.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
  };

  const formatCompactDate = (date) => {
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const renderStars = (rating) => {
    if (!rating) return null;
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  const toggleSpotExpansion = (spotId) => {
    setExpandedSpots(prev => ({
      ...prev,
      [spotId]: !prev[spotId]
    }));
  };

  const handleViewSpotDetails = (spot) => {
    actions.setSelectedSpot(spot);
    actions.setShowSpotDetails(true);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">Chargement de vos visites...</div>
        </CardContent>
      </Card>
    );
  }

  if (spotVisits.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-gray-500">
            <Eye className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>Aucune visite enregistrée</p>
            <p className="text-sm mt-1">Marquez un spot comme "visité" pour voir vos visites ici</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Mes visites ({spotVisits.reduce((total, spot) => total + spot.visitCount, 0)} visites sur {spotVisits.length} spots)
          </CardTitle>
        </CardHeader>
      </Card>

      {spotVisits.map((spotVisit) => {
        const spot = spotVisit.spot;
        const isExpanded = expandedSpots[spot.id];
        const spotImage = spot.photos && spot.photos.length > 0 ? spot.photos[0].url : null;
        
        return (
          <Card key={spot.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="space-y-4">
                {/* Spot Header with Title */}
                <div className="mb-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {spot.name || spot.title}
                  </h3>
                  
                  {/* Mini-frame with image and location */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center gap-4">
                      {/* Spot Image */}
                      {spotImage && (
                        <div className="w-20 h-20 flex-shrink-0">
                          <img
                            src={spotImage}
                            alt={spot.name || spot.title}
                            className="w-full h-full object-cover rounded-lg"
                          />
                        </div>
                      )}
                      
                      {/* Spot Information */}
                      <div className="flex-1">
                        {spot.location && (
                          <div className="flex items-center gap-2 text-gray-600 mb-2">
                            <MapPin className="h-4 w-4" />
                            <span className="text-sm">{spot.location}</span>
                          </div>
                        )}
                        
                        {spot.description && (
                          <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                            {spot.description}
                          </p>
                        )}
                        
                        {/* Tags */}
                        {spot.tags && spot.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {spot.tags.slice(0, 3).map((tag, index) => (
                              <Badge key={index} variant="secondary" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                            {spot.tags.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{spot.tags.length - 3}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* View Spot Button */}
                      <div className="flex-shrink-0">
                        <Button
                          variant="outline"
                          onClick={() => handleViewSpotDetails(spot)}
                          className="flex items-center gap-2 bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700 hover:text-blue-800"
                        >
                          <ExternalLink className="h-4 w-4" />
                          <span className="text-sm font-medium">Voir le spot</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Visit Summary and Expansion Controls */}
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                    <div className="flex items-center gap-3">
                      <Eye className="h-5 w-5 text-green-600" />
                      <div>
                        <span className="font-semibold text-green-800 text-base">
                          {spotVisit.visitCount === 1 ? '1 visite' : `${spotVisit.visitCount} visites`}
                        </span>
                        {spotVisit.firstVisitDate && spotVisit.lastVisitDate && (
                          <div className="text-sm text-green-600">
                            {spotVisit.visitCount === 1 ? (
                              `Le ${formatCompactDate(spotVisit.firstVisitDate)}`
                            ) : (
                              `Du ${formatCompactDate(spotVisit.firstVisitDate)} au ${formatCompactDate(spotVisit.lastVisitDate)}`
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <Button
                      variant="ghost"
                      onClick={() => toggleSpotExpansion(spot.id)}
                      className="flex items-center gap-2 text-green-700 hover:text-green-800 hover:bg-green-100 p-2 rounded-md transition-colors"
                    >
                      {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      <span className="text-sm font-medium">
                        {isExpanded ? 'Masquer les détails' : 'Voir le détail des visites'}
                      </span>
                    </Button>
                  </div>
                </div>

                {/* Expanded Visit Details */}
                {isExpanded && (
                  <div className="space-y-4 border-t border-green-200 pt-4 bg-gradient-to-b from-green-25 to-white rounded-b-lg">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-gray-800 text-base mb-3 flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-green-600" />
                        Historique détaillé des visites ({spotVisit.visitCount})
                      </h4>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleSpotExpansion(spot.id)}
                        className="text-xs"
                      >
                        Masquer
                      </Button>
                    </div>
                    
                    {spotVisit.visits.map((visit, index) => {
                      const visitDateTime = formatVisitDate(visit.visit_date || visit.created_at);
                      
                      return (
                        <div key={`${visit.id || index}-${visit.visit_date}`} className="bg-white border-2 border-green-100 rounded-lg p-4 shadow-md hover:shadow-lg transition-shadow">
                          {/* Visit Header */}
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-4 text-sm text-gray-700">
                              <div className="flex items-center gap-1 bg-green-50 px-2 py-1 rounded-md">
                                <Calendar className="h-4 w-4 text-green-600" />
                                <span className="font-semibold text-green-800">{visitDateTime.date}</span>
                              </div>
                              <div className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded-md">
                                <Clock className="h-4 w-4 text-gray-600" />
                                <span className="font-medium">{visitDateTime.time}</span>
                              </div>
                            </div>

                            {/* Rating */}
                            {visit.rating && (
                              <div className="flex flex-col items-end gap-1">
                                {renderStars(visit.rating)}
                                <span className="text-xs text-gray-500">{visit.rating}/5</span>
                              </div>
                            )}
                          </div>

                          {/* Weather */}
                          {visit.weather && (
                            <div className="flex items-center gap-2 mb-3">
                              <Cloud className="h-4 w-4 text-blue-500" />
                              <Badge variant="outline" className="text-xs">
                                {visit.weather}
                              </Badge>
                            </div>
                          )}

                          {/* Description */}
                          {visit.description && (
                            <div className="bg-gray-50 rounded-lg p-3 mb-3">
                              <div className="flex items-center gap-2 mb-2">
                                <MessageCircle className="h-4 w-4 text-gray-500" />
                                <span className="text-sm font-medium text-gray-700">Note de visite</span>
                              </div>
                              <p className="text-gray-700 text-sm leading-relaxed">{visit.description}</p>
                            </div>
                          )}

                          {/* Visit Photos */}
                          {visit.photos && visit.photos.length > 0 && (
                            <div className="mb-3">
                              <div className="flex items-center gap-2 mb-2">
                                <Camera className="h-4 w-4 text-gray-500" />
                                <span className="text-sm font-medium text-gray-700">Photos de la visite</span>
                              </div>
                              <div className="flex gap-2 flex-wrap">
                                {visit.photos.map((photo, photoIndex) => (
                                  <div key={photoIndex} className="w-16 h-16">
                                    <img
                                      src={photo.url || photo}
                                      alt={`Photo de visite ${photoIndex + 1}`}
                                      className="w-full h-full object-cover rounded border-2 border-gray-200"
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}